import { useCallback } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import { PaginationParams } from 'enums/paginationParams';
import { useQueryParams } from 'hooks/common';

interface PaginationQueryParamsReturn {
  paginationQueryParams: URLSearchParams;
  handlePaginationQueryParamsChange: ({
    page,
    pageSize,
    filters,
    sortBy,
  }: {
    page?: number;
    pageSize?: number;
    filters?: PaginationFilter[];
    sortBy?: PaginationSort[];
  }) => void;
  filterQueryParamsStringify: (filters: PaginationFilter[]) => string;
  filterQueryParamsParse: (filters: string) => PaginationFilter[];
  sortByQueryParamsParse: (filters: string) => PaginationSort[];
}

export const usePaginationQueryParams = (): PaginationQueryParamsReturn => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const paginationQueryParams = useQueryParams();

  const filterQueryParamsStringify = (filters: PaginationFilter[] = []): string => {
    const filtersString = filters
      .map(
        filter =>
          `${filter.columnId}${filter.condition ? `[${filter.condition}]` : ''}:${filter.value}`,
      )
      .join('~');

    return filtersString;
  };

  const filterQueryParamsParse = (filters: string): PaginationFilter[] => {
    const filterStrings = filters ? filters.split('~') : [];
    const formattedFilters = filterStrings.map(item => {
      let columnId, condition, value;
      if (item.includes(']:')) {
        columnId = item.substring(0, item.indexOf('['));
        condition = item.substring(item.indexOf('[') + 1, item.indexOf(']'));
        value = item.substring(item.indexOf(':') + 1, item.length);
      } else {
        columnId = item.substring(0, item.indexOf(':'));
        value = item.substring(item.indexOf(':') + 1, item.length);
      }

      const filter: PaginationFilter = { columnId, value };

      if (condition) {
        filter.condition = condition;
      }

      return filter;
    });

    return formattedFilters;
  };

  const sortByQueryParamsStringify = (sortBy: PaginationSort[] = []): string => {
    const sortByString = sortBy.flatMap(sort => `${sort.columnId}:${sort.order}`).join(',');

    return sortByString;
  };

  const sortByQueryParamsParse = (sortBy: string): PaginationSort[] => {
    const sortByStrings = sortBy ? sortBy.split(',') : [];
    const formattedSortBy = sortByStrings.map(item => {
      const itemParts = item.split(':');

      return {
        columnId: itemParts[0],
        order: itemParts[1],
      } as PaginationSort;
    });

    return formattedSortBy;
  };

  const handlePaginationQueryParamsChange = useCallback(
    ({
      page,
      pageSize,
      filters,
      sortBy,
    }: {
      page?: number;
      pageSize?: number;
      filters?: PaginationFilter[];
      sortBy?: PaginationSort[];
    }) => {
      if (page) {
        paginationQueryParams.set(PaginationParams.Page, page?.toString());
      }

      if (pageSize) {
        paginationQueryParams.set(PaginationParams.PageSize, pageSize?.toString());
      }

      if (filters?.length) {
        paginationQueryParams.set(PaginationParams.Filter, filterQueryParamsStringify(filters));
      } else if (filters?.length === 0) {
        paginationQueryParams.delete(PaginationParams.Filter);
      }

      if (sortBy?.length) {
        paginationQueryParams.set(PaginationParams.SortBy, sortByQueryParamsStringify(sortBy));
      } else if (sortBy?.length === 0) {
        paginationQueryParams.delete(PaginationParams.SortBy);
      }

      if (paginationQueryParams.toString() !== search) {
        navigate({
          pathname,
          search: `?${paginationQueryParams.toString()}`,
        });
      }
    },
    [navigate, paginationQueryParams, search, pathname],
  );

  return {
    paginationQueryParams,
    handlePaginationQueryParamsChange,
    filterQueryParamsStringify,
    filterQueryParamsParse,
    sortByQueryParamsParse,
  };
};
